import { request } from '@/api/client'

/**
 * إشعارات على الجهاز (owner, 2026-10-01: "شغلي push notifications ع مشروع"): this device subscribes to the gateway's
 * pushes (backend src/modules/back-office/adminRoutes.ts) through the app's own service worker (public/push-sw.js). The gateway decides who
 * gets what — the same people the bell shows an alert to.
 */
/** `noWorker`: the app's service worker is not running in this tab yet — a reload brings it. */
export type PushState = 'unsupported' | 'noWorker' | 'denied' | 'off' | 'on'

const b64uToBytes = (s: string) => {
  const pad = '='.repeat((4 - (s.length % 4)) % 4)
  const bin = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from(bin, (c) => c.charCodeAt(0))
}

export const pushSupported = () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window

/**
 * The app's service worker, or null. `serviceWorker.ready` never settles when no worker was registered (an old tab, a
 * private window), which left the settings card on «…» with no button at all (owner, 2026-10-01: "مفيش ابعت تجربة").
 */
async function registration(): Promise<ServiceWorkerRegistration | null> {
  const now = await navigator.serviceWorker.getRegistration().catch(() => undefined)
  if (now?.active) return now
  return Promise.race([navigator.serviceWorker.ready, new Promise<null>((r) => setTimeout(() => r(null), 4000))])
}

async function send(sub: PushSubscription) {
  const j = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } }
  await request('/backend/push/subscribe', { method: 'POST', body: { endpoint: j.endpoint, keys: j.keys } })
}

export const staffPushService = {
  async state(): Promise<PushState> {
    if (!pushSupported()) return 'unsupported'
    if (Notification.permission === 'denied') return 'denied'
    const reg = await registration()
    if (!reg) return 'noWorker'
    const sub = await reg.pushManager.getSubscription()
    return sub && Notification.permission === 'granted' ? 'on' : 'off'
  },

  /** Asks the browser, then registers this device with the gateway. */
  async enable(): Promise<PushState> {
    if (!pushSupported()) return 'unsupported'
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off'
    const { data } = await request<{ publicKey: string; devices: number }>('/backend/push')
    const reg = await registration()
    if (!reg) return 'noWorker'
    let sub = await reg.pushManager.getSubscription()
    // a subscription made for another server key cannot be used: replace it
    const want = b64uToBytes(data.publicKey)
    const have = sub?.options.applicationServerKey ? new Uint8Array(sub.options.applicationServerKey) : null
    if (sub && have && (have.length !== want.length || have.some((b, i) => b !== want[i]))) { await sub.unsubscribe(); sub = null }
    sub = sub ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: want }))
    await send(sub)
    return 'on'
  },

  async disable(): Promise<PushState> {
    if (!pushSupported()) return 'unsupported'
    const sub = await (await registration())?.pushManager.getSubscription()
    if (sub) {
      await request('/backend/push/unsubscribe', { method: 'POST', body: { endpoint: sub.endpoint } }).catch(() => undefined)
      await sub.unsubscribe().catch(() => undefined)
    }
    return 'off'
  },

  test: () => request<{ sent: number; failed: number }>('/backend/push/test', { method: 'POST', body: {} }).then((r) => r.data),

  /** On every app open: the device is told again who it belongs to and what its user may read today. */
  async refresh(): Promise<void> {
    if (!pushSupported() || Notification.permission !== 'granted') return
    const sub = await (await registration())?.pushManager.getSubscription()
    if (sub) await send(sub).catch(() => undefined)
  },
}
